// ============================================================
// useShownDocs - a collection of the GM's documents, each shown to the
// table or kept (clocks, the board).
//
// One listener. The GM lists everything; a player lists what names them
// (the query the rules can prove). `put` and `remove` are the GM's, applied
// here at once and then written.
//
// A READ IS NOT THE WHOLE TRUTH WHILE MY OWN WRITES ARE IN FLIGHT (gotcha
// 9): a view taken mid-burst lists only some of them. So what this screen
// wrote and has not yet seen back is kept over the read (`pending`), and a
// thing it deleted stays deleted until a read agrees (`gone`). A partial
// read (`listsEveryone` false) only adds and removes what it names.
//
// Outside a session the list is local: there is nobody to show it to.
// ============================================================
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { byCreation, shownDoc } from '../engine/narrative/shown';
import type { Shown } from '../engine/narrative/shown';
import { diag, errorSummary } from '../diagnostics/log';

type Entries = { id: string; data: Record<string, unknown> }[];

export interface ShownSource {
  watch(
    sessionId: string,
    uid: string | null,
    callback: (read: { entries: Entries; listsEveryone: boolean; removed: string[] }) => void,
  ): () => void;
  put(sessionId: string, id: string, doc: { data: string; audience: string[] }): Promise<void>;
  remove(sessionId: string, id: string): Promise<void>;
}

interface Params<T extends Shown> {
  sessionId: string | null | undefined;
  /** This seat's account; null until signed in. */
  uid: string | null;
  isGm: boolean;
  source: ShownSource;
  /** Validate one stored payload; null when this build cannot read it. */
  read: (id: string, raw: unknown) => T | null;
  /** The word the diagnostic log files a failed write under. */
  label: string;
}

export interface ShownDocs<T extends Shown> {
  /** Oldest first. */
  items: T[];
  put(item: T): void;
  remove(id: string): void;
}

/** What this screen wrote and no read has confirmed yet: each write with the text it stored. */
interface InFlight<T> {
  pending: Map<string, { item: T; data: string }>;
  gone: Set<string>;
}

function parse<T>(entries: Entries, read: (id: string, raw: unknown) => T | null): Map<string, T> {
  const out = new Map<string, T>();
  for (const entry of entries) {
    try {
      const item = read(entry.id, JSON.parse(entry.data.data as string));
      if (item) out.set(entry.id, item);
    } catch {
      // an unparseable document is not one of these
    }
  }
  return out;
}

/**
 * A read has caught up with a write once it carries the very text that was
 * stored (the column is text, so both backends hand it back unchanged), and
 * with a delete once a whole view no longer lists the document.
 */
function confirm<T>(inFlight: InFlight<T>, read: { entries: Entries; listsEveryone: boolean }) {
  const stored = new Map(read.entries.map((e) => [e.id, e.data.data]));
  for (const [id, mine] of [...inFlight.pending]) if (stored.get(id) === mine.data) inFlight.pending.delete(id);
  if (!read.listsEveryone) return;
  for (const id of [...inFlight.gone]) if (!stored.has(id)) inFlight.gone.delete(id);
}

/** The list after a read: the read (whole or partial) under this screen's unconfirmed writes. */
function merged<T>(
  was: Map<string, T>,
  seen: Map<string, T>,
  read: { listsEveryone: boolean; removed: string[] },
  inFlight: InFlight<T>,
): Map<string, T> {
  const next = read.listsEveryone ? new Map(seen) : new Map([...was, ...seen]);
  for (const id of read.removed) next.delete(id);
  for (const [id, mine] of inFlight.pending) next.set(id, mine.item);
  for (const id of inFlight.gone) next.delete(id);
  return next;
}

/** The list as reads deliver it, under this screen's unconfirmed writes. Empty outside a session. */
function useWatched<T extends Shown>(params: Params<T>, inFlight: InFlight<T>) {
  const { sessionId, uid, isGm, source, read } = params;
  const state = useState<Map<string, T>>(new Map());
  const setById = state[1];
  useEffect(() => {
    if (!sessionId || !uid) return undefined;
    let live = true;
    const unsub = source.watch(sessionId, isGm ? null : uid, (r) => {
      if (!live) return;
      const seen = parse(r.entries, read);
      confirm(inFlight, r);
      setById((was) => merged(was, seen, r, inFlight));
    });
    return () => {
      live = false;
      unsub();
      setById(new Map());
      // What was in flight belonged to the session just left: it must not overlay the next one's list.
      inFlight.pending.clear();
      inFlight.gone.clear();
    };
  }, [sessionId, uid, isGm, source, read, inFlight, setById]);
  return state;
}

const without = <T>(was: Map<string, T>, id: string): Map<string, T> => {
  const next = new Map(was);
  next.delete(id);
  return next;
};

export default function useShownDocs<T extends Shown>(params: Params<T>): ShownDocs<T> {
  const { sessionId, source, label } = params;
  const inFlight = useRef<InFlight<T>>({ pending: new Map(), gone: new Set() }).current;
  const [byId, setById] = useWatched(params, inFlight);

  /**
   * A write that was REFUSED is no longer in flight: forget it, so the next read says what the
   * server really holds. Left in place it would show an item that was never stored - or hide for
   * good one whose delete did not happen.
   */
  const failed = useCallback(
    (what: string, id: string) => (e: unknown) => {
      inFlight.pending.delete(id);
      inFlight.gone.delete(id);
      diag.error(label, what, errorSummary(e));
    },
    [label, inFlight],
  );

  const put = useCallback(
    (item: T) => {
      setById((was) => new Map(was).set(item.id, item));
      if (!sessionId) return;
      const doc = shownDoc(item);
      inFlight.gone.delete(item.id);
      inFlight.pending.set(item.id, { item, data: doc.data });
      source.put(sessionId, item.id, doc).catch(failed('a write failed', item.id));
    },
    [sessionId, source, inFlight, failed, setById],
  );

  const remove = useCallback(
    (id: string) => {
      setById((was) => without(was, id));
      if (!sessionId) return;
      inFlight.pending.delete(id);
      inFlight.gone.add(id);
      source.remove(sessionId, id).catch(failed('a delete failed', id));
    },
    [sessionId, source, inFlight, failed, setById],
  );

  const items = useMemo(() => byCreation([...byId.values()]), [byId]);
  return { items, put, remove };
}
